import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@mellify/ui';
import { PlatformIcon, platformLabels } from './PlatformIcon';
import type { SocialPlatform } from './PlatformIcon';
import { Shield, Check, ExternalLink, AlertTriangle } from 'lucide-react';

interface PermissionScope {
  label: string;
  description: string;
}

interface PermissionScopesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: () => void;
  platform: SocialPlatform;
  permissions: PermissionScope[];
  isConnecting: boolean;
}

/**
 * Platform-specific permission scope descriptions.
 * Used as fallback when the API doesn't provide them.
 */
export const defaultPlatformPermissions: Record<SocialPlatform, PermissionScope[]> = {
  facebook: [
    { label: 'Manage Pages', description: 'View and manage your Facebook Pages' },
    { label: 'Publish Posts', description: 'Create and publish posts on your Pages' },
    { label: 'Read Comments & Engagement', description: 'View comments, reactions, and engagement on your posts' },
    { label: 'Read & Respond to Messages', description: 'Access and respond to direct messages on your Pages' },
    { label: 'Read Analytics', description: 'Access Page insights and performance metrics' },
  ],
  instagram: [
    { label: 'Basic Profile Access', description: 'View your Instagram Business profile information' },
    { label: 'Publish Content', description: 'Create and publish posts and stories' },
    { label: 'Manage Comments', description: 'View and respond to comments on your posts' },
    { label: 'Read Analytics', description: 'Access account insights and post performance data' },
  ],
  x: [
    { label: 'Read Tweets', description: 'View tweets on your timeline and mentions' },
    { label: 'Post Tweets', description: 'Create, reply to, and delete tweets' },
    { label: 'Read Profile', description: 'Access your profile information and follower data' },
    { label: 'Read Direct Messages', description: 'Access direct messages for engagement monitoring' },
    { label: 'Offline Access', description: "Maintain connection when you're not actively using Mellify" },
  ],
  linkedin: [
    { label: 'Profile Access', description: 'View your LinkedIn profile information' },
    { label: 'Publish Posts', description: 'Create and share posts on your behalf' },
  ],
  youtube: [
    { label: 'Manage Channel', description: 'View and manage your YouTube channel' },
    { label: 'Upload Videos', description: 'Upload and publish videos to your channel' },
    { label: 'Read Channel Data', description: 'Access video and channel information' },
    { label: 'Read Analytics', description: 'Access YouTube Analytics data for your channel' },
  ],
  bluesky: [
    { label: 'Read Profile', description: 'View your Bluesky profile information and feed' },
    { label: 'Create Posts', description: 'Create and publish posts on your behalf' },
    { label: 'Read Followers', description: 'Access your follower and following data' },
    { label: 'Manage Content', description: 'Like, repost, and interact with content' },
  ],
  tiktok: [],
  pinterest: [],
  reddit: [
    { label: 'Read Identity', description: 'Access your Reddit username and account information' },
    { label: 'Read Content', description: 'Read posts, comments, and subreddit content' },
    { label: 'Submit Content', description: 'Submit posts and comments on your behalf' },
    { label: 'Edit Content', description: 'Edit and delete your posts and comments' },
    { label: 'Read History', description: 'Access your post and comment history' },
  ],
  devto: [
    { label: 'Read Profile', description: 'Access your DEV Community profile information' },
    { label: 'Publish Articles', description: 'Create and publish articles on your behalf' },
    { label: 'Manage Articles', description: 'Edit and update your existing articles' },
    { label: 'Read Analytics', description: 'Access article views, reactions, and comment counts' },
    { label: 'Read Followers', description: 'Access your follower information' },
  ],
  dailydev: [
    { label: 'Publish Content', description: 'Create and publish posts on daily.dev' },
    { label: 'Read Profile', description: 'Access your daily.dev profile information' },
  ],
  mastodon: [
    { label: 'Read Profile', description: 'View your Mastodon profile information and feed' },
    { label: 'Create Posts', description: 'Create and publish toots on your behalf' },
    { label: 'Upload Media', description: 'Upload images and media attachments' },
    { label: 'Read Followers', description: 'Access your follower and following data' },
    { label: 'Manage Content', description: 'Boost, favourite, and interact with content' },
  ],
  hashnode: [
    { label: 'Read Profile', description: 'Access your Hashnode profile and publication information' },
    { label: 'Publish Articles', description: 'Create and publish articles on your Hashnode blog' },
    { label: 'Manage Articles', description: 'Edit, update, and delete your existing articles' },
    { label: 'Read Analytics', description: 'Access article views, reactions, and comment counts' },
    { label: 'Manage Drafts', description: 'Create and manage draft articles' },
  ],
};

export const PermissionScopesModal: React.FC<PermissionScopesModalProps> = ({
  isOpen,
  onClose,
  onConnect,
  platform,
  permissions,
  isConnecting,
}) => {
  const displayPermissions =
    permissions.length > 0 ? permissions : defaultPlatformPermissions[platform] || [];

  // Check if this is LinkedIn and if org scopes are not included in permissions
  // (indicating only personal profile connection is available)
  const isLinkedInWithoutOrgAccess =
    platform === 'linkedin' &&
    !displayPermissions.some(
      (p) =>
        p.label.toLowerCase().includes('organization') ||
        p.label.toLowerCase().includes('company')
    );

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="md"
      closeOnOverlayClick={!isConnecting}
      closeOnEscape={!isConnecting}
      aria-labelledby="permission-scopes-title"
    >
      <ModalHeader>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-dark-700 border border-dark-200 dark:border-dark-600">
            <PlatformIcon platform={platform} size="md" />
          </div>
          <div>
            <h2
              id="permission-scopes-title"
              className="text-lg font-semibold text-dark-900 dark:text-dark-100"
            >
              Connect {platformLabels[platform]}
            </h2>
            <p className="text-xs text-dark-500 dark:text-dark-400 mt-0.5">
              Review permissions before connecting
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Security notice */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 mb-5">
          <Shield className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
              Secure Connection
            </p>
            <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
              You'll be redirected to {platformLabels[platform]} to authorize Mellify.
              We never store your password.
            </p>
          </div>
        </div>

        {/* LinkedIn Page notice - shown when org scopes are not available */}
        {isLinkedInWithoutOrgAccess && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 mb-5">
            <AlertTriangle className="w-5 h-5 text-warning-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-warning-700 dark:text-warning-300">
                Personal Profile Only
              </p>
              <p className="text-xs text-warning-600 dark:text-warning-400 mt-0.5">
                Currently, only personal LinkedIn profile connections are available.
                To connect LinkedIn Company Pages, the app administrator needs to
                enable the Community Management API in the LinkedIn Developer portal.
              </p>
            </div>
          </div>
        )}

        {/* Permissions list */}
        <div>
          <h3 className="text-sm font-medium text-dark-700 dark:text-dark-300 mb-3">
            Mellify will be able to:
          </h3>
          <div className="space-y-3">
            {displayPermissions.map((permission, index) => (
              <div
                key={index}
                className="flex items-start gap-3"
              >
                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-success-50 dark:bg-success-900/20 shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-success-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-dark-800 dark:text-dark-200">
                    {permission.label}
                  </p>
                  <p className="text-xs text-dark-500 dark:text-dark-400 mt-0.5">
                    {permission.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Data usage note */}
        <div className="mt-5 pt-4 border-t border-dark-200 dark:border-dark-700">
          <p className="text-xs text-dark-400 dark:text-dark-500">
            By connecting, you agree to allow Mellify to access the permissions listed above.
            You can disconnect this account at any time from the Channels page.
          </p>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          variant="outline"
          size="md"
          onClick={onClose}
          disabled={isConnecting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={onConnect}
          loading={isConnecting}
          rightIcon={!isConnecting ? <ExternalLink className="w-4 h-4" /> : undefined}
        >
          {isConnecting ? 'Connecting...' : 'Connect'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

PermissionScopesModal.displayName = 'PermissionScopesModal';